import { Link, useParams } from 'react-router-dom';
import Icon from '../components/Icons';
import { formatCharge, serviceOf } from '../data/common';
import { useBookings } from './BookingsContext';
import { FlowBand, TravellerTable } from './FlowParts';
import DocumentLink from '../documents/DocumentLink';

const payLabel = { upi: 'UPI', card: 'Credit Card', international: 'International Card', debit: 'Debit Card', netbanking: 'Net Banking', nb: 'Net Banking', emi: 'No-cost EMI' };
// Who finishes a pending booking, and what they send when it's ready.
const pendingText = {
  insurance: ['insurer', 'The insurer is issuing your policy', 'it is ready'],
  transfers: ['cab operator', 'The cab operator is confirming your ride', 'it is confirmed'],
  hotels: ['hotel', 'The hotel is confirming your booking — this usually takes a few minutes', 'it is confirmed'],
  bus: ['bus operator', 'The bus operator is confirming your seats', 'your ticket is confirmed'],
};
const airlinePending = ['airline', 'The airline is issuing your ticket', 'the PNR is ready'];

/** Explains how a checkout linked the booking to a customer account. */
function AccountNotice({ account }) {
  if (!account) return null;
  const { status, email, emailSent } = account;
  if (status === 'SignedIn') {
    return <div className="api-message success">This booking is saved to your account <b>{email}</b>.</div>;
  }
  if (status === 'Existing') {
    return (
      <div className="api-message">
        <b>{email}</b> already has a Trawtel account, so we added this booking to it.{' '}
        <Link className="link" to="/account/login">Sign in</Link> to manage it.
      </div>
    );
  }
  return (
    <div className="api-message success">
      We created a Trawtel account for <b>{email}</b> and signed you in.{' '}
      {emailSent
        ? 'Your temporary password has been emailed — change it after signing in.'
        : <>We couldn't email your temporary password right now; use <Link className="link" to="/account/forgot-password">Forgot password</Link> to set one.</>}
    </div>
  );
}

export default function ConfirmationPage({ module, Summary, refLabel = 'Booking reference', nextSteps = [], Extra }) {
  const { bookingId } = useParams();
  const { getBooking } = useBookings();
  const booking = getBooking(bookingId);
  const s = serviceOf(module);

  if (!booking || booking.module !== module) {
    return (
      <div className="container section empty">
        <span className="empty-icon"><Icon name="compass" size={34} /></span>
        <h2>Booking not found</h2>
        <Link to={`${s.path}/bookings`} className="btn btn-primary">Go to my {s.label.toLowerCase()} bookings</Link>
      </div>
    );
  }

  const isHeld = booking.status === 'held';
  const isPending = booking.status === 'pending';
  const [partner, pendingLead, pendingReady] = pendingText[module] || airlinePending;

  return (
    <>
      <FlowBand title={isHeld ? 'Supplier hold placed' : isPending ? 'Booking received' : 'Booking confirmed'} step={3} />
      <div className="container confirm-wrap">
        <div className="confirm-hero panel">
          <div className="confirm-icon"><Icon name={isHeld || isPending ? 'clock' : 'check'} size={34} strokeWidth={3} /></div>
          <div>
            <h2>{isHeld ? 'Your supplier hold was placed.' : isPending ? `Your booking is being confirmed with the ${partner}.` : `Your ${s.noun.toLowerCase()} booking is confirmed!`}</h2>
            <p className="muted">
              {isHeld
                ? 'This reservation is not paid or ticketed. Complete supplier confirmation before the hold expires.'
                : isPending
                  ? <>{pendingLead}. We'll email <b>{booking.contact.email}</b> as soon as {pendingReady}.</>
                  : booking.account && !booking.account.emailSent
                    ? <>Keep your booking ID handy — we couldn't email the e-ticket to <b>{booking.contact.email}</b> right now.</>
                    : <>We've sent the {module === 'insurance' ? 'policy document' : 'e-ticket / voucher'} to <b>{booking.contact.email}</b> and an SMS to {booking.contact.phone}.</>}
            </p>
          </div>
          <div className="confirm-ids">
            <div><span className="muted small">Booking ID</span><b>{booking.id}</b></div>
            <div><span className="muted small">{refLabel}</span><b>{booking.ref}</b></div>
            <div><span className="muted small">{isHeld ? 'Supplier quoted fare' : 'Amount paid'}</span><b>{formatCharge(booking.fare.total)}</b>{!isHeld && <span className="muted small">via {payLabel[booking.payment]}</span>}</div>
          </div>
        </div>

        <AccountNotice account={booking.account} />

        <div className="confirm-grid">
          <div className="confirm-main">
            <section className="panel"><Summary summary={booking.summary} /></section>
            <section className="panel">
              <h3>Travellers</h3>
              <TravellerTable travellers={booking.travellers} />
            </section>
            {Extra && <Extra booking={booking} />}
          </div>
          <aside>
            {nextSteps.length > 0 && (
              <section className="panel">
                <h3>What's next?</h3>
                <ol className="next-steps">{nextSteps.map((n) => <li key={n}>{n}</li>)}</ol>
              </section>
            )}
            <section className="panel confirm-actions">
              <Link to={`${s.path}/bookings/${booking.id}`} className="btn btn-primary block">View booking details</Link>
              <DocumentLink booking={booking} fallbackLabel={`Download ${module === 'insurance' ? 'policy' : 'voucher'}`} />
              <Link to={s.path} className="btn btn-ghost block">Book another {s.noun.toLowerCase()}</Link>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}
